import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useFillGasTablesMutation, useRecalculateGoalStatusSummaryMutation } from "@api/graphql/types-and-hooks";
import { dismissToast, notifyError, notifyWarnPersistent } from "@components/notifications/notifications";
import { ReportNode } from "@providers/ReportProvider";

// frontera FillGasTables (api/mutations/report.py) returns status true with this message when every GAS table
// filled, and otherwise a message naming the tables it left for the clinician to fill by hand (PROA-2749).
// Compared on letters and digits only, so a change in case, spacing or punctuation still reads as filled. Any
// other wording shows as a warning instead of hiding a partial fill. FillGasTables has a comment pointing here.
const normalizeMessage = (message: string) =>
  message
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const GAS_TABLES_FILLED_MESSAGE = normalizeMessage("GAS tables updated successfully.");

const gasPartialWarningId = (reportId: string) => `gas-tables-partial-${reportId}`;

const GOAL_STATUS_SUMMARY_ERROR = "An error occurred while updating the Goal Status Summary table.";

// The partial-fill warning stays open until closed, so close it when the clinician leaves the report or opens
// another one. Otherwise it reads as if it were about the next report.
export const useDismissGasPartialWarningOnLeave = (reportId: string | undefined) => {
  useEffect(() => {
    if (!reportId) return;
    return () => dismissToast(gasPartialWarningId(reportId));
  }, [reportId]);
};

export const useNavigateToReview = (report: ReportNode) => {
  const go = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { mutateAsync: fillGasTables, isPending: isFillingGasTables } = useFillGasTablesMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-report-by-id", report?.id] });
    },
  });
  const { mutateAsync: recalculateGoalStatusSummary, isPending: isRecalculatingGoalStatusSummary } =
    useRecalculateGoalStatusSummaryMutation({
      onSuccess: async (data) => {
        // Most reports have no Goal Status Summary field, so nothing changed and there is nothing to refetch.
        if (!data.recalculateGoalStatusSummary?.updated) return;
        await queryClient.invalidateQueries({ queryKey: ["get-report-by-id", report?.id] });
      },
    });

  const navigateToReview = useCallback(async () => {
    const reviewPath = pathname.replace(/[^/]+$/, "review-and-sign");

    const treatmentPlanTemplate = report.reportTemplate?.treatmentPlanTemplate;
    if (!treatmentPlanTemplate) {
      return;
    }

    // Goal Status Summary isn't gated by isGasEnabled — it's a no-op on the
    // backend when the report has no field wired to it, and must not block
    // navigation to Review-and-Sign, so a failure here only surfaces a
    // notification rather than stopping the transition.
    try {
      const response = await recalculateGoalStatusSummary({ reportId: report.id });
      if (!response.recalculateGoalStatusSummary?.status) {
        notifyError(GOAL_STATUS_SUMMARY_ERROR);
      }
    } catch (_error) {
      notifyError(GOAL_STATUS_SUMMARY_ERROR);
    }

    // Every exit except a new partial fill clears an earlier partial-fill warning, so it never lists tables
    // the clinician may have filled since.
    const warningId = gasPartialWarningId(report.id);
    if (treatmentPlanTemplate.isGasEnabled) {
      let response: Awaited<ReturnType<typeof fillGasTables>>;
      try {
        response = await fillGasTables({ reportId: report.id });
      } catch (_error) {
        dismissToast(warningId);
        notifyError("An error occurred while updating GAS Tables.");
        return;
      }
      if (!response.fillGasTables?.status) {
        dismissToast(warningId);
        notifyError("Failed to update GAS Tables.");
        return;
      }
      // Partly filled tables don't block Review, but the clinician has to see which ones to fix.
      // Kept open until closed, since the message can list several tables; a later fill replaces it.
      const message = response.fillGasTables.message?.trim();
      if (message && normalizeMessage(message) !== GAS_TABLES_FILLED_MESSAGE) {
        notifyWarnPersistent(message, warningId);
        void go(reviewPath);
        return;
      }
    }
    dismissToast(warningId);
    void go(reviewPath);
  }, [report, fillGasTables, recalculateGoalStatusSummary, go, pathname]);

  return {
    navigateToReview,
    isNavigating: isFillingGasTables || isRecalculatingGoalStatusSummary,
  };
};
